import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";

export default function Register() {
  useSeo({ title: "Créer un compte", description: "Créez votre compte propriétaire ou chercheur sur R-1.immo pour publier des annonces de sous-sols ou trouver un lieu compatible avec votre activité.", path: "/register" });
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    const r = await register(form);
    setLoading(false);
    if (r.ok) {
      toast.success("Compte créé");
      nav(form.role === "owner" ? "/deposer" : "/annonces");
    } else {
      setErr(r.error);
    }
  };

  return (
    <div className="min-h-[80vh] px-6 md:px-12 lg:px-16 py-16 flex flex-col items-center justify-center">
      <div className="w-full max-w-md border border-zinc-800 bg-zinc-950 p-8">
        <div className="label-mono mb-2 text-amber-500">§ inscription</div>
        <h1 className="font-display text-3xl mb-8">Créer votre compte</h1>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label-mono block mb-2">Nom complet</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name" className="w-full bg-zinc-900 border border-zinc-800 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <div>
            <label className="label-mono block mb-2">Email</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" className="w-full bg-zinc-900 border border-zinc-800 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <div>
            <label className="label-mono block mb-2">Mot de passe (min. 6 caractères)</label>
            <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password" className="w-full bg-zinc-900 border border-zinc-800 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <div>
            <label className="label-mono block mb-2">Vous êtes</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setForm({ ...form, role: "user" })} data-testid="role-user" className={`py-3 text-xs uppercase tracking-widest border ${form.role === "user" ? "border-amber-500 text-amber-500 bg-amber-500/10" : "border-zinc-800 text-zinc-400"}`}>
                Chercheur
              </button>
              <button type="button" onClick={() => setForm({ ...form, role: "owner" })} data-testid="role-owner" className={`py-3 text-xs uppercase tracking-widest border ${form.role === "owner" ? "border-amber-500 text-amber-500 bg-amber-500/10" : "border-zinc-800 text-zinc-400"}`}>
                Propriétaire
              </button>
            </div>
          </div>
          {err && <div className="text-sm text-red-400 label-mono">{err}</div>}
          <button type="submit" disabled={loading} data-testid="register-submit" className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black py-3 text-xs uppercase tracking-widest transition-colors">
            {loading ? "Création…" : "Créer le compte"}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-zinc-800 text-sm text-zinc-400 text-center">
          Déjà un compte ? <Link to="/login" className="text-amber-500">Se connecter</Link>
        </div>
      </div>
      <div className="w-full max-w-md mt-10 text-sm text-zinc-500 leading-relaxed space-y-3">
        <p><strong className="text-zinc-300">R-1.immo</strong> est la première marketplace française dédiée aux sous-sols, caves, parkings, réserves et locaux techniques atypiques. Chaque année, des milliers de m² dorment sous les immeubles français : nous les remettons en circulation.</p>
        <p><strong className="text-zinc-300">Propriétaire ?</strong> Publiez votre lieu en 9 étapes guidées et recevez des demandes qualifiées d'exploitants (escape games, dark kitchens, studios, événementiel, showrooms). <strong className="text-zinc-300">Exploitant ?</strong> Filtrez le catalogue selon votre activité et vos contraintes techniques : hauteur, accès, ERP, ventilation, puissance électrique.</p>
        <p>L'inscription est gratuite. Aucune commission cachée. Contact direct entre propriétaire et exploitant.</p>
      </div>
    </div>
  );
}
